import { useState } from 'react';

import { useSnackbar } from 'notistack';

import {
  AccomPropertyImage,
  fetchAccomPropertyImages as fetchAccomPropertyImagesShared,
  isPropertyNotMappedError,
} from '~/utils/accommodationPropertyImageSync';
import getErrorMessage from '~/utils/getErrorMessage';
import { reportError } from '~/utils/reportError';

const SYNC_TOP_PROPERTY_IMAGE_COUNT = 10;

interface UseLpcPropertyImageSyncParams {
  propertyId?: string;
  propertyName?: string;
  offerImages: Array<{ id_cloudinary_external: string }>;
  onAddImage: (cloudinaryId: string) => Promise<unknown>;
}

function getNotMappedMessage(action: 'sync' | 'import', label: string): string {
  return `Can't ${action} images: ${label} is not mapped to an accommodation property`;
}

interface BatchImportOutcome {
  successCount: number;
  failedCount: number;
}

function reportBatchImportResult(
  results: Array<PromiseSettledResult<unknown>>,
  enqueueSnackbar: ReturnType<typeof useSnackbar>['enqueueSnackbar'],
  messages: {
    allFailed: (failedCount: number, firstError: unknown) => string;
    partialFailure: (successCount: number, failedCount: number) => { message: string; variant: 'error' | 'warning' };
    allSucceeded: (successCount: number) => string;
  },
): BatchImportOutcome {
  const successCount = results.filter((result) => result.status === 'fulfilled').length;
  const failedCount = results.length - successCount;

  results.forEach((result) => {
    if (result.status === 'rejected' && result.reason instanceof Error) {
      reportError(result.reason);
    }
  });

  if (successCount === 0 && failedCount > 0) {
    const firstFailure = results.find((result) => result.status === 'rejected');
    enqueueSnackbar(messages.allFailed(failedCount, firstFailure.reason), { variant: 'error' });
  } else if (failedCount > 0) {
    const { message, variant } = messages.partialFailure(successCount, failedCount);
    enqueueSnackbar(message, { variant });
  } else if (successCount > 0) {
    enqueueSnackbar(messages.allSucceeded(successCount), { variant: 'success' });
  }

  return { successCount, failedCount };
}

export default function useLpcPropertyImageSync({
  propertyId,
  propertyName,
  offerImages,
  onAddImage,
}: UseLpcPropertyImageSyncParams) {
  const { enqueueSnackbar } = useSnackbar();
  const [isSyncingPropertyImages, setIsSyncingPropertyImages] = useState(false);
  const [isLoadingImportImages, setIsLoadingImportImages] = useState(false);
  const [isImportingSelectedImages, setIsImportingSelectedImages] = useState(false);
  const [isImportImagesModalVisible, setIsImportImagesModalVisible] = useState(false);
  const [accomPropertyImages, setAccomPropertyImages] = useState<Array<AccomPropertyImage>>([]);
  const [selectedImportImageIds, setSelectedImportImageIds] = useState<Array<string>>([]);

  const fetchAccomPropertyImages = async (): Promise<Array<AccomPropertyImage>> => {
    if (!propertyId) {
      return [];
    }

    const existingPublicIds = new Set(offerImages.map((image) => image.id_cloudinary_external));
    return fetchAccomPropertyImagesShared(propertyId, existingPublicIds);
  };

  const syncTopPropertyImages = async () => {
    if (isSyncingPropertyImages || offerImages.length >= SYNC_TOP_PROPERTY_IMAGE_COUNT) {
      return;
    }

    const label = propertyName || 'the property';
    setIsSyncingPropertyImages(true);

    try {
      if (!propertyId) {
        enqueueSnackbar(getNotMappedMessage('sync', label), { variant: 'error' });
        return;
      }

      const images = await fetchAccomPropertyImages();
      const remainingSlots = Math.max(0, SYNC_TOP_PROPERTY_IMAGE_COUNT - offerImages.length);
      const newImages = images.filter((image) => !image.alreadyOnOffer).slice(0, remainingSlots);

      if (newImages.length === 0) {
        enqueueSnackbar(`0 new images synced from ${label}`, { variant: 'info' });
        return;
      }

      const results = await Promise.allSettled(newImages.map((image) => onAddImage(image.publicId)));
      reportBatchImportResult(results, enqueueSnackbar, {
        allFailed: (_failedCount, firstError) => `Failed to sync images from ${label}: ${getErrorMessage(firstError)}`,
        partialFailure: (syncedCount) => ({
          message: `Only synced ${syncedCount} image${syncedCount === 1 ? '' : 's'} from ${label}; some imports failed`,
          variant: 'error',
        }),
        allSucceeded: (syncedCount) => `Synced ${syncedCount} ${syncedCount === 1 ? 'image' : 'images'} from ${label}`,
      });
    } catch (error) {
      if (isPropertyNotMappedError(error)) {
        enqueueSnackbar(getNotMappedMessage('sync', label), { variant: 'error' });
        return;
      }

      enqueueSnackbar(`Failed to sync images from ${label}: ${getErrorMessage(error)}`, { variant: 'error' });
    } finally {
      setIsSyncingPropertyImages(false);
    }
  };

  const toggleImportImageSelection = (imageId: string) => {
    setSelectedImportImageIds((current) =>
      current.includes(imageId) ? current.filter((id) => id !== imageId) : current.concat(imageId),
    );
  };

  const showImportImagesModal = async () => {
    if (isLoadingImportImages) {
      return;
    }

    const label = propertyName || 'the property';
    setIsLoadingImportImages(true);

    try {
      if (!propertyId) {
        enqueueSnackbar(getNotMappedMessage('import', label), { variant: 'error' });
        return;
      }

      const images = await fetchAccomPropertyImages();
      setAccomPropertyImages(images);
      setSelectedImportImageIds([]);

      if (images.length === 0) {
        enqueueSnackbar('No property images found to import', { variant: 'info' });
        return;
      }

      setIsImportImagesModalVisible(true);
    } catch (error) {
      if (isPropertyNotMappedError(error)) {
        enqueueSnackbar(getNotMappedMessage('import', label), { variant: 'error' });
        return;
      }

      enqueueSnackbar(`Failed to load property images: ${getErrorMessage(error)}`, { variant: 'error' });
    } finally {
      setIsLoadingImportImages(false);
    }
  };

  const hideImportImagesModal = () => {
    setIsImportImagesModalVisible(false);
    setSelectedImportImageIds([]);
  };

  const importSelectedPropertyImages = async () => {
    if (isImportingSelectedImages) {
      return;
    }

    const selectedIdSet = new Set(selectedImportImageIds);
    const selectedImages = accomPropertyImages.filter(
      (image) => selectedIdSet.has(image.publicId) && !image.alreadyOnOffer,
    );

    setIsImportingSelectedImages(true);

    try {
      const results = await Promise.allSettled(selectedImages.map((image) => onAddImage(image.publicId)));
      reportBatchImportResult(results, enqueueSnackbar, {
        allFailed: (failedCount) => `Failed to import ${failedCount === 1 ? 'image' : `${failedCount} images`}`,
        partialFailure: (importedCount, failedCount) => ({
          message: `Imported ${importedCount} ${importedCount === 1 ? 'image' : 'images'} (${failedCount} failed)`,
          variant: 'warning',
        }),
        allSucceeded: (importedCount) => `Imported ${importedCount} ${importedCount === 1 ? 'image' : 'images'}`,
      });
    } finally {
      setIsImportingSelectedImages(false);
      hideImportImagesModal();
    }
  };

  const importableImages = accomPropertyImages.map((image) => ({
    id: image.id,
    imageId: image.publicId,
    title: image.title ?? null,
    alreadyOnOffer: image.alreadyOnOffer,
  }));
  const alreadyOnOfferIdSet = new Set(
    importableImages.filter((image) => image.alreadyOnOffer).map((image) => image.imageId),
  );

  return {
    syncTopPropertyImageCount: SYNC_TOP_PROPERTY_IMAGE_COUNT,
    isSyncCapReached: offerImages.length >= SYNC_TOP_PROPERTY_IMAGE_COUNT,
    isSyncingPropertyImages,
    isLoadingImportImages,
    isImportingSelectedImages,
    isImportImagesModalVisible,
    importableImages,
    alreadyOnOfferIdSet,
    selectedImportImageIds,
    syncTopPropertyImages,
    showImportImagesModal,
    hideImportImagesModal,
    toggleImportImageSelection,
    importSelectedPropertyImages,
  };
}
